import { prefersClassicBusinessUi } from '~/services/business-ui/preference';
import { useAuthStore } from '~/stores/auth';
import { useBusinessStore } from '~/stores/business';

/**
 * Business middleware
 * Ensures user is authenticated and has access to the business
 */
export default defineNuxtRouteMiddleware(async (to) => {
    const authStore = useAuthStore();
    const businessStore = useBusinessStore();

    // Check if user is authenticated
    if (!authStore.loggedIn) {
        const redirectCookie = useCookie('pb-redirect');
        redirectCookie.value = to.fullPath;

        let loginUrl = '/auth/login';
        if (to.query.email) {
            loginUrl += `?email=${encodeURIComponent(to.query.email as string)}`;
        }

        return navigateTo(loginUrl);
    }

    // Get the business slug from the route
    const slug = to.params.slug as string;

    if (!slug) {
        // No slug provided, redirect to home
        return navigateTo('/');
    }

    // Check for company ID in query param (used when navigating from partner companies)
    const companyId = to.query.cid ? Number(to.query.cid) : undefined;

    // `user` is the member the global route guard confirmed before this middleware runs
    // (middleware/routing.global.ts): the decisions below read that member's /me.

    // Dark launch: the hive business portal is PrivacyBee-staff-only until
    // GA — everyone else continues to the legacy business app, same as
    // before this portal existed. The cookie is a veto a staff member can set
    // to work in the legacy app; it can never grant entry, so a customer who
    // sets it by hand still lands exactly where they do today. Delete the
    // is_pb clause at GA; the grants inside the try below are the permanent gate.
    if (!authStore.user?.identity?.is_pb || prefersClassicBusinessUi()) {
        const legacyDomain = (useRuntimeConfig().public.pb as any).appDomainLegacy;
        return navigateTo(`${legacyDomain}/business/${slug}/dashboard`, { external: true });
    }

    try {
        // Fetch business data (pass companyId if available for partner company navigation)
        await businessStore.fetchBusiness(slug, false, companyId);

        // Check if user has access
        const user = authStore.user;
        const isPbAdmin = user?.identity?.is_pb;
        const hasEmployerAccess = user?.employer?.slug === slug;

        // Entry gate for `/business/*`. Any of these grants is enough to
        // pass this middleware; per-page checks (e.g.,
        // `partner/companies.vue` requiring `partner`/`businesses`,
        // `partner/index.vue` requiring `partner-analytics`) enforce
        // the narrower scope so analytics-only users don't leak into
        // the companies surface.
        const permissions = user?.employer?.permissions || [];
        const hasPartnerAccess = permissions.includes('read partner') ||
            permissions.includes('write partner') ||
            permissions.includes('read businesses') ||
            permissions.includes('write businesses');

        // `partner-analytics` is a narrow, standalone grant. It must NOT open
        // the whole `/business/*` surface (dashboard/financial/employees/etc.)
        // — it only admits the partner routes (`/business/:slug/partner*`),
        // where the per-page gates then apply. Scoping it to the target route
        // keeps analytics-only users out of unrelated business data while
        // leaving broad `partner`/`businesses` access unchanged.
        const isPartnerRoute = to.path.startsWith(`/business/${slug}/partner`);
        const hasPartnerAnalyticsAccess = isPartnerRoute &&
            permissions.includes('partner-analytics');

        if (!isPbAdmin && !hasEmployerAccess && !hasPartnerAccess && !hasPartnerAnalyticsAccess) {
            // User doesn't have access to this business
            console.warn(`User doesn't have access to business: ${slug}`);
            return navigateTo('/');
        }
    } catch (error) {
        console.error('Failed to load business:', error);
        return navigateTo('/');
    }
});
